/**
 * @file        src/pages/hotel/UsedCouponList.jsx
 * @description Used Coupon List — hotel bookings that applied a coupon
 *              (`/hotel/used-coupons`). Legacy parity columns: # / Coupon Code /
 *              Booking ID / Total Amount / Discount Amount / Discount Type /
 *              Discount / Booking Status.
 *
 *              "Discount Amount" is the rupees actually taken off THIS booking;
 *              "Discount" + "Discount Type" are the coupon's configured rule
 *              (joined from coupon_codes), so a 10% coupon reads 10 / percentage
 *              even though every booking discounts a different amount.
 * @author      Kinetika Labs
 * @created     2026-07-02
 * @changelog
 *   - 2026-07-02 Claude — Initial (B2C Hotel Management).
 *   - 2026-07-12 Claude — Legacy parity columns; added the coupon's Discount Type
 *                          + configured Discount and the Booking Status.
 *   - 2026-08-10 Claude — Added title/subtitle/audience props (defaulting to the
 *                          unchanged B2C behaviour) so CorporateUsedCouponList.jsx
 *                          can reuse this page fixed to audience=b2b instead of
 *                          forking it — the backend now actually applies
 *                          `audience` (it previously ignored it and had no
 *                          `users` join at all).
 */
import ReadOnlyListPage from '../flight/ReadOnlyListPage';
import Chip from '../../components/Chip';
import { money, statusTone } from './cells';

export default function UsedCouponList({
  title = 'Used Coupon List',
  subtitle = 'B2C Hotel · coupons applied to bookings',
  audience = 'b2c',
} = {}) {
  return (
    <ReadOnlyListPage
      title={title}
      subtitle={subtitle}
      endpoint="/hotel/used-coupons"
      queryRoot={`hotel-used-coupons-${audience}`}
      extraParams={{ audience }}
      searchLabel="Coupon Code"
      columns={[
        { key: 'coupon_code', label: 'Coupon Code', render: (v) => <b className="mono">{v || '—'}</b> },
        { key: 'booking_reference', label: 'Booking ID', render: (v, r) => <span className="mono">{v || `#${r.id}`}</span> },
        { key: 'total_amount', label: 'Total Amount', render: (_v, r) => money(r.total_paid) },
        { key: 'coupon_discount', label: 'Discount Amount', render: (v) => (v ? money(v) : '—') },
        { key: 'discount_type', label: 'Discount Type', render: (v) => (v ? <Chip tone="warn">{v}</Chip> : '—') },
        {
          key: 'discount_value',
          label: 'Discount',
          // The coupon's configured rule — 10% or ₹500 — not the per-booking rupees.
          render: (v, r) => (v == null ? '—' : r.discount_type === 'percentage' ? `${v}%` : money(v)),
        },
        { key: 'booking_status', label: 'Booking Status', render: (v) => (v ? <Chip tone={statusTone(v)}>{v}</Chip> : '—') },
      ]}
    />
  );
}
